import { getImpersonationOverride } from '@/common/hooks/useImpersonation'
import { getMockTimeOverride } from '@/common/hooks/useMockTime'

/**
 * Shared fetch wrapper that includes credentials and injects the
 * X-Mock-Time and X-Impersonate-Employee-Id headers when active.
 */
export async function fetchApi<T>(url: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers)

  const mockTime = getMockTimeOverride()
  if (mockTime) {
    headers.set('X-Mock-Time', mockTime)
  }

  const impersonateId = getImpersonationOverride()
  if (impersonateId) {
    headers.set('X-Impersonate-Employee-Id', impersonateId)
  }

  const res = await fetch(url, {
    credentials: 'include',
    ...init,
    headers,
  })

  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.detail || `Request failed: ${res.status}`)
  }

  return res.json()
}

/**
 * Fetch a file (blob) response and trigger a browser download. Reuses the same
 * credentials and X-Mock-Time / X-Impersonate-Employee-Id headers as fetchApi.
 * The filename from the server's Content-Disposition header is preferred, with
 * fallbackFilename used when absent.
 */
export async function downloadFile(url: string, fallbackFilename: string): Promise<void> {
  const headers = new Headers()

  const mockTime = getMockTimeOverride()
  if (mockTime) {
    headers.set('X-Mock-Time', mockTime)
  }

  const impersonateId = getImpersonationOverride()
  if (impersonateId) {
    headers.set('X-Impersonate-Employee-Id', impersonateId)
  }

  const res = await fetch(url, { credentials: 'include', headers })

  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.detail || `Request failed: ${res.status}`)
  }

  const disposition = res.headers.get('Content-Disposition') ?? ''
  const match = /filename="?([^";]+)"?/.exec(disposition)
  const filename = match ? match[1] : fallbackFilename

  const blob = await res.blob()
  const objectUrl = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = objectUrl
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(objectUrl)
}
